/**
 * @file MovementsTable.tsx
 * @description Журнал движений таблицей на `≥lg` (phase19.24 этап 1, шаг 2).
 *
 * `DocumentItemsTable` здесь не подходит: его каркас начинается с колонки фото
 * 80px и заканчивается меню «⋯», а у движения нет ни картинки, ни действий —
 * две пустые колонки съели бы 152px и вытолкнули «Кол-во» за край. Каркас
 * повторяем правилами контракта: `table-fixed` + `colgroup` (длинное название
 * товара не растягивает колонку) и `th relative` у `sr-only` (иначе подпись
 * позиционируется от документа и тянет горизонтальный скролл страницы).
 *
 * Колонка «Основание» вместо «Документ»: кладовщик спрашивает «почему остаток
 * изменился», а ответ — это номер документа плюс комментарий движения.
 *
 * 1024–1279 (phase19.31, приёмка qa4): колонке содержимого ~700px не хватало
 * на семь столбцов — таблица уезжала вбок, «Кол-во» пряталось за край. На
 * этой ширине «Склад» уходит второй линией под операцию, «Партия» — под
 * артикул; с 1280 — обычные столбцы. Номер основания — с областью нажатия
 * 44px без смены вида строки (`fit="cell"`).
 */

import { MovementBasis, MovementLot, MovementQuantity } from "./MovementCells";
import { movementOperationText } from "../../../lib/movements-texts";
import { LIST_SURFACE_CLASS } from "../../ui/ListStates";
import { formatSkuName, type StockMovementDto } from "../../../lib/api-wms";
import { useMediaQuery, WIDE_QUERY } from "../../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";

interface Props {
  items: StockMovementDto[];
  /** Slug магазина — нужен ссылкам на документы-основания. */
  slug?: string;
}

/** Дата и время движения: «03.09.2026, 10:12». */
function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Подпись вторым цветом под основной линией ячейки. */
const SECOND_LINE = "truncate text-xs text-on-surface-variant";

export function MovementsTable({ items, slug }: Props) {
  // С 1280 — семь столбцов; уже — «Склад» и «Партия» второй линией.
  const wide = useMediaQuery(WIDE_QUERY);
  return (
    // SC69: таблица на непрозрачной поверхности — ленты фона кабинета
    // просвечивали сквозь строки журнала (замечание владельца 2026-10-03).
    <div className={cn("hidden overflow-x-auto rounded-xl ring-ghost lg:block", LIST_SURFACE_CLASS)}>
      <table className="w-full table-fixed text-sm" style={wide ? { minWidth: "900px" } : undefined}>
        <colgroup>
          <col className={wide ? "w-[140px]" : "w-[108px]"} />
          <col className={wide ? "w-[150px]" : "w-[140px]"} />
          <col className={wide ? "w-[26%]" : undefined} />
          {wide ? <col className="w-[130px]" /> : null}
          {wide ? <col className="w-[110px]" /> : null}
          <col className={wide ? "w-[19%]" : "w-[170px]"} />
          <col className={wide ? "w-[100px]" : "w-[72px]"} />
        </colgroup>
        <thead className="bg-on-surface/5 text-left">
          <tr className="label-editorial">
            <th className="relative px-3 py-3">Дата</th>
            <th className="relative px-3 py-3">Операция</th>
            <th className="relative px-3 py-3">Товар / артикул</th>
            {wide ? <th className="relative px-3 py-3">Склад</th> : null}
            {wide ? <th className="relative px-3 py-3">Партия</th> : null}
            <th className="relative px-3 py-3">Основание</th>
            <th className="relative px-3 py-3 text-right">Кол-во</th>
          </tr>
        </thead>
        <tbody>
          {items.map((movement) => (
            <tr
              key={movement.id}
              className="border-t border-on-surface/8 align-top transition-colors hover:bg-[var(--row-hover)]"
            >
              <td className="px-3 py-3 text-on-surface-variant tabular-nums">
                {formatMoment(movement.createdAt)}
              </td>
              <td className="px-3 py-3">
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span>{movementOperationText(movement)}</span>
                  {wide ? null : <span className={SECOND_LINE}>{movement.warehouse?.name ?? "—"}</span>}
                </span>
              </td>
              <td className="px-3 py-3">
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium">{formatSkuName(movement.sku)}</span>
                  {/* Артикул — снимок на момент движения (ФЗ-402 ст. 9):
                      карточку могли переименовать, запись читается как есть. */}
                  <span className={cn(SECOND_LINE, "font-mono")}>
                    {movement.skuCode}
                    {wide || !movement.lot ? null : (
                      <>
                        {" · "}
                        <MovementLot movement={movement} />
                      </>
                    )}
                  </span>
                </span>
              </td>
              {wide ? (
                <td className="truncate px-3 py-3 text-on-surface-variant">{movement.warehouse?.name ?? "—"}</td>
              ) : null}
              {wide ? (
                <td className="truncate px-3 py-3 text-xs text-on-surface-variant">
                  <MovementLot movement={movement} />
                </td>
              ) : null}
              <td className="px-3 py-3">
                <MovementBasis movement={movement} slug={slug} fit="cell" />
              </td>
              <td className="px-3 py-3 text-right">
                <MovementQuantity quantity={movement.quantity} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
